import * as React from 'react';
import * as THREE from 'three';
import {GLTFLoader, type GLTF} from 'three/examples/jsm/loaders/GLTFLoader';
import {
    CabinetMesh,
    LayoutRoomPlaceholder,
} from 'components/customer/RoomPlanner/types';
import {
    setCabinetPositioningFlags,
    getTargetPositionNextToCabinet,
    clearCabinetPositioningFlags,
} from 'components/customer/RoomPlanner/helpers/cabinetSideButtonHelpers';
import {
    rotatingSet,
    registerPlaceholderId,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {store} from 'store/customer/storeSetup';
import {getRoomDimensionsComputed} from 'components/customer/RoomPlanner/helpers/getComputedDimension';
import {DEFAULT_POSITION_Y} from 'components/customer/RoomPlanner/helpers/renderCabinetGroup';
import {
    DUPLICATE_PLACEMENT_GAP,
    DUPLICATE_FLOOR_WALL_MARGIN,
    DUPLICATE_FLOOR_BOUNDS_PADDING,
    getDuplicateFloorPlacementBounds,
} from 'components/customer/RoomPlanner/helpers/cabinetPlacement';
import {computeWorldBoundsExcludingAnimatedDoorParts} from 'components/customer/RoomPlanner/helpers/calculations';
import Camera from 'components/customer/RoomPlanner/lib/use3DRPCameraSetup';
import {attachPlaceholderHighlight} from 'components/customer/RoomPlanner/helpers/placeholder/highlight';
import {autoSave} from 'components/customer/RoomPlanner/lib/useAutoSave';
import {placeholderFaceApi} from 'components/customer/RoomPlanner/store/placeholderFaceApi';
import {PlaceholderFace} from 'components/customer/RoomPlanner/entity/PlaceholderFace';
import {syncPlaceholderProductEvents} from 'components/customer/RoomPlanner/helpers/placeholder/syncEvents';
import {applyPlaceholderVerticalPlacement} from 'components/customer/RoomPlanner/helpers/placeholder/height';
import {containPlaceholderInRoom} from 'components/customer/RoomPlanner/helpers/placeholder/containInRoom';
import {
    placeholderHangsOnWall,
    resolvePlaceholderStyle,
} from 'components/customer/RoomPlanner/helpers/placeholder/style';
import {
    determinePlaceholderSpawnLocation,
    resolvePlaceholderSpawnOverlaps,
} from 'components/customer/RoomPlanner/helpers/placeholder/spawn';
import {
    clearPlaceholdersFromScene,
    removePlaceholderFromScene,
} from 'components/customer/RoomPlanner/helpers/placeholder/removePlaceholder';
import {
    getPlaceholderCopyIndex,
    getPlaceholderGroupId,
    getMaxPlaceholderCopyIndex,
    clampPlaceholderQuantity,
    findPlaceholderGroupOf,
    makePlaceholderCopyId,
    nextPlaceholderGroupId,
    parsePlaceholderIdentity,
    placeholderSceneId,
    setPlaceholderGroupQuantityValue,
    syncPlaceholderQuantitiesFromGroups,
} from 'components/customer/RoomPlanner/helpers/placeholder/quantity';

export {syncPlaceholderProductEvents};

type PlaceholderMesh = THREE.Mesh<
    THREE.BufferGeometry,
    THREE.Material | THREE.Material[]
>;

/**
 * @param {THREE.Object3D} object Scene object
 * @return {boolean} Whether the object is a typed mesh
 */
const isPlaceholderMesh = (object: THREE.Object3D): object is PlaceholderMesh =>
    object instanceof THREE.Mesh;

/**
 * @param {PlaceholderMesh} mesh Mesh whose materials to collect
 * @return {THREE.Material[]} Material list
 */
const getMeshMaterials = (mesh: PlaceholderMesh): THREE.Material[] =>
    Array.isArray(mesh.material) ? mesh.material : [mesh.material];

/** Fainter GLB materials are raised to this so the product stays visible. */
const MIN_PLACEHOLDER_OPACITY = 0.5;

const PLACEHOLDER_COLOR_STORAGE_PREFIX = 'gocabinets.placeholderColor.';

/**
 * Drop leftover tints from the removed placeholder color picker.
 * @return {void}
 */
const clearStoredPlaceholderColors = (): void => {
    try {
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(PLACEHOLDER_COLOR_STORAGE_PREFIX)) {
                keys.push(key);
            }
        }
        keys.forEach((key) => localStorage.removeItem(key));
    } catch {
        // private mode / quota
    }
};

/** Toggle Redux `isRotating` so useRoomLayout rebuilds dimension/dashed overlays. */
const setRotating = () => {
    const isRotating = store.getState().roomPlanner.isRotating;
    store.dispatch(rotatingSet(!isRotating));
};

const gltfLoader = new GLTFLoader();
const gltfByUrl = new Map<string, Promise<GLTF>>();

const loadGltf = (url: string): Promise<GLTF> => {
    const cached = gltfByUrl.get(url);
    if (cached !== undefined) return cached;

    const pending = new Promise<GLTF>((resolve, reject) => {
        gltfLoader.load(url, resolve, undefined, reject);
    });
    gltfByUrl.set(url, pending);
    pending.catch(() => {
        if (gltfByUrl.get(url) === pending) {
            gltfByUrl.delete(url);
        }
    });
    return pending;
};

const clonePlaceholderScene = (gltf: GLTF): CabinetMesh => {
    const group = gltf.scene.clone(true) as CabinetMesh;
    group.traverse((child) => {
        if (
            child instanceof THREE.Mesh &&
            child.geometry instanceof THREE.BufferGeometry
        ) {
            child.geometry = child.geometry.clone();
        }
    });
    return group;
};

export const PLACEHOLDER_CONFIGS: PlaceholderConfig[] = [
    {
        url: '/templates/3D/models/fridge.glb',
        id: 'placeholder-fridge',
        targetHeightMm: 2000,
        initialX: -200,
        initialZ: 300,
    },
    {
        url: '/templates/3D/models/oven.glb',
        id: 'placeholder-oven',
        targetHeightMm: 900,
        initialX: 200,
        initialZ: 300,
    },
];

/**
 * Place fridge.glb and oven.glb in the same folder as adjustable_leg.glb and inner_drawer.glb:
 * /templates/3D/models/
 */
export interface PlaceholderConfig {
    /** URL to the GLB model (e.g. /templates/3D/models/fridge.glb) */
    url: string;
    /** Base id: 'placeholder-fridge' | 'placeholder-oven' | `placeholder-{id}` */
    id: string;
    /**
     * Optional height-fit in mm (legacy fridge/oven). When omitted, the GLB is
     * placed at uniform PLACEHOLDER_SCENE_SCALE (native GLB units treated as mm).
     */
    targetHeightMm?: number;
    /** Optional exact width in mm (applied after load) */
    targetWidthMm?: number;
    /** Optional exact depth in mm (applied after load) */
    targetDepthMm?: number;
    /** Display name from admin Placeholder Face */
    name?: string;
    /** Category from admin Placeholder Face */
    category?: string;
    /** Cabinets.style: 1 Base (floor), 3 Upper (wall) */
    style?: number;
    /** Admin Placeholder Face id (for API persistence) */
    placeholderFaceId?: number;
    /** Initial X position in scene coordinates (optional) */
    initialX?: number;
    /** Initial Z position in scene coordinates (optional) */
    initialZ?: number;
}

/**
 * Id prefix used for instance ids (`{base}-1`, `{base}-2`, ...).
 * @param {string} configId - Config id (`placeholder-1`, `placeholder-fridge`, ...)
 * @return {string} Base prefix
 */
export const getPlaceholderIdBase = (configId: string): string =>
    configId.startsWith('placeholder-')
        ? configId.replace(/^placeholder-/, '')
        : configId;

/**
 * Next unique instance id. Uses max existing suffix + 1 so deletes cannot
 * recreate a colliding id (count-based ids were breaking click/drag).
 * @param {string} base - Id prefix (e.g. face id `1`, or `fridge`)
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef - Placeholder list
 * @return {string} e.g. `1-3`
 */
export const getNextPlaceholderInstanceId = (
    base: string,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>
): string =>
    nextPlaceholderGroupId(
        base,
        (placeholderListRef.current ?? []).map((item) =>
            String(item.userData?.id || '')
        )
    );

/**
 * Get the next unique id for a placeholder (e.g. 'fridge-1', '1-2').
 * @param {PlaceholderConfig} config - The placeholder config.
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef - The list of placeholders.
 * @return {string} The next unique id.
 */
function getNextPlaceholderId(
    config: PlaceholderConfig,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>
): string {
    return getNextPlaceholderInstanceId(
        getPlaceholderIdBase(config.id),
        placeholderListRef
    );
}

export type PlaceholderUserData = {
    id?: string | number;
    isPlaceholder?: boolean;
    isUpperProduct?: boolean;
    isBaseProduct?: boolean;
    isFloorProduct?: boolean;
    sitOnFloor?: boolean;
    placeholderFaceId?: number;
    placeholderName?: string;
    placeholderCategory?: string;
    placeholderStyle?: number;
    placeholderTemplate?: string;
    placeholderGroupId?: string;
    /** Layout placement id, once the placement has been saved. */
    layoutPlaceholderId?: number;
    currentDirection?: string;
    values?: {
        actualDimensions?: PlaceholderDimensionsMm;
        cabinet_width?: number;
        cabinet_height?: number;
        cabinet_depth?: number;
        quantity?: number;
        duplicate_id?: string;
        id?: string;
    };
};

/**
 * Rebuild a load config from a live placeholder mesh (duplicate / undo).
 * @param {CabinetMesh} group Placeholder group
 * @return {PlaceholderConfig | null} Config or null when the mesh has no template
 */
export const configFromPlaceholderMesh = (
    group: CabinetMesh
): PlaceholderConfig | null => {
    const userData = group.userData as PlaceholderUserData;
    const placeholderId = String(userData.id ?? '');
    const dims = userData.values?.actualDimensions;

    if (userData.placeholderTemplate) {
        const base =
            userData.placeholderFaceId != null
                ? String(userData.placeholderFaceId)
                : placeholderId.replace(/-\d+$/, '') || placeholderId;
        return {
            url: userData.placeholderTemplate,
            id: `placeholder-${base}`,
            targetHeightMm: dims?.height,
            targetWidthMm: dims?.width,
            targetDepthMm: dims?.depth,
            name: userData.placeholderName,
            category: userData.placeholderCategory,
            style: userData.placeholderStyle,
            placeholderFaceId: userData.placeholderFaceId,
        };
    }

    const baseId = placeholderId.startsWith('fridge')
        ? 'fridge'
        : placeholderId.startsWith('oven')
          ? 'oven'
          : null;
    if (!baseId) return null;
    const config = PLACEHOLDER_CONFIGS.find(
        (item) => item.id === `placeholder-${baseId}`
    );
    return config ? {...config} : null;
};

type CommitPlaceholderOptions = {
    persist?: boolean;
    sync?: boolean;
    resetPointer?: boolean;
};

const commitPlaceholder = (
    group: CabinetMesh,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    options: CommitPlaceholderOptions = {}
): void => {
    const {persist = true, sync = true, resetPointer = false} = options;
    group.userData.savedPosition = group.position.clone();
    group.updateMatrixWorld(true);
    if (!placeholderListRef.current.includes(group)) {
        placeholderListRef.current.push(group);
    }
    if (persist) {
        autoSave(group, true);
    }
    if (sync) {
        syncPlaceholderProductEvents(cabinetGroupListRef, {resetPointer});
    }
};

type AddPlaceholderOptions = {
    sync?: boolean;
    /** Explicit client id. Defaults to a new group with copy index 0. */
    id?: string;
    /** Product quantity stored on this copy (same on every visual copy). */
    quantity?: number;
};

const stampPlaceholderProductIdentity = (
    group: CabinetMesh,
    id: string,
    quantity: number
): void => {
    const identity = parsePlaceholderIdentity(id);
    const groupId = identity.groupId || id;
    group.userData.id = id;
    (group.userData as PlaceholderUserData).placeholderGroupId = groupId;
    syncPlaceholderCabinetDimensions(group, getPlaceholderDimensionsMm(group), {
        quantity,
    });
    const values = (group.userData.values || {}) as Record<string, unknown>;
    values.duplicate_id = id;
    values.id = groupId;
    values.quantity = quantity;
    group.userData.values = values as unknown as typeof group.userData.values;
};

const applyConfigDimensions = (
    group: CabinetMesh,
    config: PlaceholderConfig
): void => {
    if (
        config.targetWidthMm == null ||
        config.targetHeightMm == null ||
        config.targetDepthMm == null
    ) {
        return;
    }
    applyPlaceholderDimensions(group, {
        width: config.targetWidthMm,
        height: config.targetHeightMm,
        depth: config.targetDepthMm,
    });
};

export const PLACEHOLDER_SCENE_SCALE = 0.2;

type PlaceholderNativeSize = {x: number; y: number; z: number};

/**
 * glTF is usually meters; some CAD exports are cm. Room Planner cabinets use mm×0.2.
 * Detect GLB units so a 0.9m cooker is placed at 900mm, not 0.9mm.
 * Used only for the initial load scale — `get`/`apply` always use raw native
 * AABB because THREE.js scale is applied to that geometry, not to mm.
 * @param {THREE.Vector3} nativeSize - Unscaled local AABB size
 * @return {number} Multiplier to convert native units → mm
 */
export const getGlbUnitToMm = (nativeSize: THREE.Vector3): number => {
    const maxDim = Math.max(nativeSize.x, nativeSize.y, nativeSize.z);
    if (!(maxDim > 0) || !Number.isFinite(maxDim)) return 1;
    if (maxDim < 20) return 1000; // meters (e.g. 0.6–2.0)
    if (maxDim < 250) return 10; // centimeters (e.g. 60–200)
    return 1; // already millimetres
};

/**
 * Scene scale × native extent. `unitToMm` must not appear here: it is already
 * baked into the group's scale on load (meters GLB → scale 200, not 0.2).
 * Multiplying again turned 900mm into 900000mm on add.
 * @param {number} scale Axis scale on the placeholder group
 * @param {number} native Unscaled GLB extent on that axis
 * @return {number} Size in millimetres
 */
const nativeAxisToMm = (scale: number, native: number): number =>
    (scale * native) / PLACEHOLDER_SCENE_SCALE;

/**
 * Inverse of `nativeAxisToMm`. Do not divide by `unitToMm` — that shrinks a
 * 0.9m GLB to 0.9mm when restoring a saved 900mm size.
 * @param {number} mm Target size in millimetres
 * @param {number} native Unscaled GLB extent on that axis
 * @return {number} Scale to set on the group
 */
const mmToNativeScale = (mm: number, native: number): number =>
    (mm * PLACEHOLDER_SCENE_SCALE) / Math.max(native, 1e-6);

export type PlaceholderDimensionsMm = {
    width: number;
    height: number;
    depth: number;
};

/**
 * Values ≥ 20m are not kitchen appliances; they are 900mm saved as 900000
 * from the old double `unitToMm` read.
 * @param {PlaceholderDimensionsMm} dimensionsMm Stored or API millimetres
 * @return {PlaceholderDimensionsMm} Millimetres safe to apply as scene size
 */
export const normalizePlaceholderDimensionsMm = (
    dimensionsMm: PlaceholderDimensionsMm
): PlaceholderDimensionsMm => {
    const max = Math.max(
        dimensionsMm.width,
        dimensionsMm.height,
        dimensionsMm.depth
    );
    if (!(max >= 20000)) return {...dimensionsMm};
    return {
        width: Math.round(dimensionsMm.width / 1000),
        height: Math.round(dimensionsMm.height / 1000),
        depth: Math.round(dimensionsMm.depth / 1000),
    };
};

/**
 * Store placeholder sizes using the same fields cabinets use
 * (`cabinet_width` / `cabinet_height` / `cabinet_depth`) plus `actualDimensions`.
 * @param {CabinetMesh} group Placeholder group
 * @param {PlaceholderDimensionsMm} dimensionsMm Target millimetre sizes
 * @param {Object=} extras Optional quantity override
 * @return {void}
 */
export const syncPlaceholderCabinetDimensions = (
    group: CabinetMesh,
    dimensionsMm: PlaceholderDimensionsMm,
    extras?: {quantity?: number}
): void => {
    const current = (group.userData.values || {}) as Record<string, unknown>;
    const quantity =
        extras?.quantity != null
            ? extras.quantity
            : Math.max(1, Number(current.quantity) || 1);

    group.userData.values = {
        ...current,
        actualDimensions: {...dimensionsMm},
        // Same keys as floor cabinets so dimension arrows / layout code apply.
        cabinet_width: dimensionsMm.width,
        cabinet_height: dimensionsMm.height,
        cabinet_depth: dimensionsMm.depth,
        width: dimensionsMm.width,
        height: dimensionsMm.height,
        depth: dimensionsMm.depth,
        quantity,
    } as unknown as typeof group.userData.values;
};

/**
 * Read placeholder W/H/D in mm from current local scale (rotation-independent).
 * Prefers live scale × nativeSize; falls back to stored cabinet_* / actualDimensions.
 * @param {CabinetMesh} group Placeholder group
 * @return {PlaceholderDimensionsMm} Width, height and depth in millimetres
 */
export const getPlaceholderDimensionsMm = (
    group: CabinetMesh
): PlaceholderDimensionsMm => {
    const userData = group.userData as CabinetMesh['userData'] & {
        nativeSize?: PlaceholderNativeSize;
        unitToMm?: number;
        values?: {
            actualDimensions?: PlaceholderDimensionsMm;
            cabinet_width?: number;
            cabinet_height?: number;
            cabinet_depth?: number;
        };
    };

    const native = userData.nativeSize;
    if (native && native.x > 0 && native.y > 0 && native.z > 0) {
        return {
            width: Math.round(nativeAxisToMm(group.scale.x, native.x)),
            height: Math.round(nativeAxisToMm(group.scale.y, native.y)),
            depth: Math.round(nativeAxisToMm(group.scale.z, native.z)),
        };
    }

    const values = userData.values;
    const fromCabinetFields = {
        width: Number(values?.cabinet_width),
        height: Number(values?.cabinet_height),
        depth: Number(values?.cabinet_depth),
    };
    if (
        Number.isFinite(fromCabinetFields.width) &&
        fromCabinetFields.width > 0 &&
        Number.isFinite(fromCabinetFields.height) &&
        fromCabinetFields.height > 0 &&
        Number.isFinite(fromCabinetFields.depth) &&
        fromCabinetFields.depth > 0
    ) {
        return normalizePlaceholderDimensionsMm(fromCabinetFields);
    }

    const stored = values?.actualDimensions;
    if (
        stored &&
        Number.isFinite(stored.width) &&
        Number.isFinite(stored.height) &&
        Number.isFinite(stored.depth)
    ) {
        return normalizePlaceholderDimensionsMm({...stored});
    }

    const size = new THREE.Box3()
        .setFromObject(group)
        .getSize(new THREE.Vector3());
    return {
        width: Math.round(size.x / PLACEHOLDER_SCENE_SCALE),
        height: Math.round(size.y / PLACEHOLDER_SCENE_SCALE),
        depth: Math.round(size.z / PLACEHOLDER_SCENE_SCALE),
    };
};

/**
 * Pull the mesh inside the room and rebuild overlays when the pose changed.
 * @param {CabinetMesh} group Placeholder mesh
 * @return {void}
 */
const containChangedPlaceholder = (group: CabinetMesh): void => {
    const before = group.position.clone();
    containPlaceholderInRoom(group);
    if (group.position.distanceTo(before) > 1e-3) {
        setRotating();
    }
};

type RestoredPlaceholderPose = {
    position: {x: number; y: number; z: number};
    rotation: {x: number; y: number; z: number};
    currentDirection?: string | null;
    actualDimensions?: {width: number; height: number; depth: number};
    quantity?: number;
};

/**
 * Apply a saved size, then the saved pose. Scaling first and then copying
 * the stored position avoids treating the catalog-sized mesh's underside as
 * the resized one — that dropped a shorter appliance through the floor on
 * reload. The final contain pulls a pose that still crosses a wall back in.
 * @param {CabinetMesh} group Freshly loaded placeholder
 * @param {RestoredPlaceholderPose} pose Position, rotation, and size from the layout API
 * @return {void}
 */
export const applyRestoredPlaceholderPose = (
    group: CabinetMesh,
    pose: RestoredPlaceholderPose
): void => {
    if (pose.currentDirection) {
        group.userData.currentDirection = pose.currentDirection;
    }
    group.rotation.set(pose.rotation.x, pose.rotation.y, pose.rotation.z);

    if (pose.actualDimensions) {
        applyPlaceholderDimensions(group, pose.actualDimensions);
        syncPlaceholderCabinetDimensions(
            group,
            pose.actualDimensions,
            pose.quantity != null ? {quantity: pose.quantity} : undefined
        );
    } else if (pose.quantity != null) {
        syncPlaceholderCabinetDimensions(
            group,
            getPlaceholderDimensionsMm(group),
            {quantity: pose.quantity}
        );
    }

    group.position.set(pose.position.x, pose.position.y, pose.position.z);
    group.updateMatrixWorld(true);
    containChangedPlaceholder(group);
    group.userData.savedPosition = group.position.clone();
};

/**
 * Scale a placeholder so its local AABB matches the given mm dimensions
 * end-to-end. Each axis is independent: editing width only changes scale.x.
 * Keeps the current mesh-bottom height (floor appliances stay on the floor;
 * rangehoods stay at their hanging height), then pulls the mesh back inside
 * the room when the new size crosses a wall, the floor, or the ceiling.
 * @param {CabinetMesh} group - Placeholder mesh
 * @param {{width: number, height: number, depth: number}} dimensionsMm - Target size in mm
 */
export const applyPlaceholderDimensions = (
    group: CabinetMesh,
    dimensionsMm: {width: number; height: number; depth: number}
): void => {
    const userData = group.userData as CabinetMesh['userData'] & {
        nativeSize?: PlaceholderNativeSize;
        unitToMm?: number;
    };
    const target = normalizePlaceholderDimensionsMm(dimensionsMm);

    let native = userData.nativeSize;
    if (!native || native.x <= 0 || native.y <= 0 || native.z <= 0) {
        // Recover native size for placeholders loaded before nativeSize existed.
        const prevScale = group.scale.clone();
        group.scale.set(1, 1, 1);
        group.updateMatrixWorld(true);
        const size = new THREE.Box3()
            .setFromObject(group)
            .getSize(new THREE.Vector3());
        native = {
            x: Math.max(size.x, 1e-6),
            y: Math.max(size.y, 1e-6),
            z: Math.max(size.z, 1e-6),
        };
        userData.nativeSize = native;
        group.scale.copy(prevScale);
    }

    group.updateMatrixWorld(true);
    const previousBottom =
        computeWorldBoundsExcludingAnimatedDoorParts(group).min.y;

    group.scale.set(
        mmToNativeScale(target.width, native.x),
        mmToNativeScale(target.height, native.y),
        mmToNativeScale(target.depth, native.z)
    );
    group.updateMatrixWorld(true);

    const worldBottom =
        computeWorldBoundsExcludingAnimatedDoorParts(group).min.y;
    if (Number.isFinite(previousBottom) && Number.isFinite(worldBottom)) {
        group.position.y += previousBottom - worldBottom;
    }
    group.updateMatrixWorld(true);

    // Keep cabinet_* fields in sync so layout/dimension code matches cabinets.
    syncPlaceholderCabinetDimensions(group, target);
    containChangedPlaceholder(group);
};

/**
 * Loads a GLB model and returns a CabinetMesh configured as a placeholder.
 * Placeholders support dragging but not editing, and are not included in the cabinet list.
 * @param {PlaceholderConfig} config - The placeholder configuration.
 * @param {THREE.Scene} scene - The Three.js scene to add the placeholder to.
 * @return {Promise<CabinetMesh>} A promise that resolves to the loaded placeholder mesh.
 */
export const loadPlaceholderGLB = (
    config: PlaceholderConfig,
    scene: THREE.Scene
): Promise<CabinetMesh> => {
    return loadGltf(config.url).then((gltf) => {
        const group = clonePlaceholderScene(gltf);
        // Reset rotation before measuring so native local axes match
        // width (X) / height (Y) / depth (Z) for later non-uniform edits.
        group.rotation.set(0, 0, 0);
        group.quaternion.identity();
        group.scale.set(1, 1, 1);
        group.updateMatrixWorld(true);

        const bbox = new THREE.Box3().setFromObject(group);
        const nativeSize = new THREE.Vector3();
        bbox.getSize(nativeSize);
        const bboxHeight = Math.max(nativeSize.y, 1e-6);

        // Height-fit / unit detection only sets the initial scale. get/apply
        // then use raw native AABB — never multiply unitToMm again.
        if (config.targetHeightMm != null && config.targetHeightMm > 0) {
            const modelScale =
                (config.targetHeightMm * PLACEHOLDER_SCENE_SCALE) / bboxHeight;
            group.scale.set(modelScale, modelScale, modelScale);
        } else {
            const unitToMm = getGlbUnitToMm(nativeSize);
            const modelScale = unitToMm * PLACEHOLDER_SCENE_SCALE;
            group.scale.set(modelScale, modelScale, modelScale);
        }
        group.updateMatrixWorld(true);

        // Recalculate bounding box after scaling to get accurate dimensions
        const scaledBbox = new THREE.Box3().setFromObject(group);
        const scaledSize = new THREE.Vector3();
        scaledBbox.getSize(scaledSize);
        const bottomOffset = scaledBbox.min.y; // Distance from origin to bottom

        // World scene units → mm (same factor cabinets use: 1/0.2 = 5)
        const actualDimensions = {
            width: Math.round(scaledSize.x / PLACEHOLDER_SCENE_SCALE),
            height: Math.round(scaledSize.y / PLACEHOLDER_SCENE_SCALE),
            depth: Math.round(scaledSize.z / PLACEHOLDER_SCENE_SCALE),
        };

        // Calculate Y position same as floor cabinets: -(wallHeight / 2) + sitOnFloor
        // Try to get wall height from existing cabinets first, fallback to computed dimensions
        let wallHeight: number;
        const existingCabinets = scene.children.filter(
            (child) =>
                child.userData?.wallHeight &&
                typeof child.userData.wallHeight === 'number'
        ) as CabinetMesh[];
        if (existingCabinets.length > 0) {
            wallHeight = Number(existingCabinets[0].userData.wallHeight);
        } else {
            const dimensions = getRoomDimensionsComputed();
            wallHeight = dimensions.wallHeight;
        }

        // Calculate base Y position for floor level
        const basePositionY = -(wallHeight / 2) + DEFAULT_POSITION_Y.sitOnFloor;

        // Adjust Y position by subtracting the bottom offset so the bottom sits on the floor
        const positionY = basePositionY - bottomOffset;

        group.position.set(
            config.initialX ?? 0,
            positionY,
            config.initialZ ?? 0
        );

        group.traverse((child) => {
            if (!isPlaceholderMesh(child) || !child.material) {
                return;
            }

            const preparedMaterials: THREE.Material[] = getMeshMaterials(
                child
            ).map((source) => {
                // Clone so shared GLB materials are not mutated across instances.
                const mat = source.clone();

                if (
                    mat instanceof THREE.MeshStandardMaterial ||
                    mat instanceof THREE.MeshPhysicalMaterial
                ) {
                    // High metalness + no environment map renders nearly black
                    // in Room Planner (ambient/directional lights only).
                    if (mat.metalness > 0.35) {
                        mat.metalness = 0.15;
                    }
                    if (mat.roughness < 0.25) {
                        mat.roughness = 0.45;
                    }
                    if (mat.map) {
                        mat.map.colorSpace = THREE.SRGBColorSpace;
                    }
                    if (
                        mat.color.r === 0 &&
                        mat.color.g === 0 &&
                        mat.color.b === 0 &&
                        !mat.map
                    ) {
                        mat.color.set('#c8c8c8');
                    }
                    mat.emissiveIntensity = 0;
                } else if (mat instanceof THREE.MeshPhongMaterial) {
                    if (mat.map) {
                        mat.map.colorSpace = THREE.SRGBColorSpace;
                    }
                }

                // Keep the GLB opacity, but never fainter than 60%.
                if (mat.opacity < MIN_PLACEHOLDER_OPACITY) {
                    mat.opacity = MIN_PLACEHOLDER_OPACITY;
                }
                if (mat.opacity < 1) {
                    mat.transparent = true;
                }
                // The GLB blend mode turns depth writing off, so the front
                // and back of the box both show. A back face sitting on a
                // wall plane also flickers against that wall. Write depth,
                // keep the outside faces, and bias the mesh in front of the wall.
                mat.depthWrite = true;
                mat.side = THREE.FrontSide;
                mat.polygonOffset = true;
                mat.polygonOffsetFactor = -1;
                mat.polygonOffsetUnits = -1;
                mat.needsUpdate = true;
                return mat;
            });

            child.material =
                preparedMaterials.length === 1
                    ? preparedMaterials[0]
                    : preparedMaterials;

            attachPlaceholderHighlight(child);
        });

        const directions = ['front', 'left', 'back', 'right'] as const;
        const rotateAroundCenter = (grp: THREE.Group, angle: number) => {
            const box = new THREE.Box3().setFromObject(grp);
            const oldCenter = box.getCenter(new THREE.Vector3());
            grp.rotation.y += angle;
            grp.updateMatrixWorld();
            const newBox = new THREE.Box3().setFromObject(grp);
            const newCenter = newBox.getCenter(new THREE.Vector3());
            const offset = newCenter.clone().sub(oldCenter);
            grp.position.sub(offset);
            grp.updateMatrixWorld();
        };

        const style = resolvePlaceholderStyle(config.style, config.category);
        const hangsOnWall = placeholderHangsOnWall(
            config.style,
            config.category
        );
        const identity = parsePlaceholderIdentity(config.id);

        group.userData = {
            ...(group.userData || {}),
            id: config.id,
            placeholderGroupId: identity.groupId || config.id,
            savedPosition: group.position.clone(),
            isPlaceholder: true,
            isUpperProduct: hangsOnWall,
            isBaseProduct: !hangsOnWall,
            isFloorProduct: !hangsOnWall,
            sitOnFloor: !hangsOnWall,
            isDoorOrDrawerOpen: false,
            ignoreOverlap: false,
            currentDirection: 'front',
            wallHeight: wallHeight,
            placeholderFaceId: config.placeholderFaceId,
            placeholderName: config.name,
            placeholderCategory: config.category,
            placeholderStyle: style,
            placeholderTemplate: config.url,
            // Unscaled local AABB (GLB units). Scale already includes unit
            // conversion, so get/apply treat unitToMm as 1.
            nativeSize: {
                x: Math.max(nativeSize.x, 1e-6),
                y: Math.max(nativeSize.y, 1e-6),
                z: Math.max(nativeSize.z, 1e-6),
            },
            unitToMm: 1,
            values: {
                actualDimensions,
                cabinet_width: actualDimensions.width,
                cabinet_height: actualDimensions.height,
                cabinet_depth: actualDimensions.depth,
                width: actualDimensions.width,
                height: actualDimensions.height,
                depth: actualDimensions.depth,
                quantity: 1,
                duplicate_id: config.id,
                id: identity.groupId || config.id,
            },
            move: (pos: THREE.Vector3): void => {
                group.position.copy(pos);
            },
            onHide: (): void => {
                group.visible = false;
            },
            rotateLeft: (): void => {
                // Same as cabinets: toggle rotating so dimension arrows
                // and floor dashed guides re-sync in useRoomLayout.
                setRotating();
                const currentDirection =
                    group.userData.currentDirection || 'front';
                const idx = (directions as readonly string[]).indexOf(
                    currentDirection
                );
                const currentIndex = idx >= 0 ? idx : 0;
                group.userData.currentDirection =
                    directions[(currentIndex + 1) % 4];
                rotateAroundCenter(group, Math.PI / 2);
                autoSave(group);
            },
            rotateRight: (): void => {
                // Same as cabinets: toggle rotating so dimension arrows
                // and floor dashed guides re-sync in useRoomLayout.
                setRotating();
                const currentDirection =
                    group.userData.currentDirection || 'front';
                const idx = (directions as readonly string[]).indexOf(
                    currentDirection
                );
                const currentIndex = idx >= 0 ? idx : 0;
                group.userData.currentDirection =
                    directions[(currentIndex + 3) % 4];
                rotateAroundCenter(group, -Math.PI / 2);
                autoSave(group);
            },
            duplicateCabinet: async (): Promise<void> => {
                // Placeholder - no-op
            },
            deleteCabinet: (): undefined => undefined,
            disposeDimensionArrows: (): undefined => undefined,
            disposeMovementArrows: (): undefined => undefined,
            disposeWallDistanceArrows: (): undefined => undefined,
            adaptToWallHeightChange: (): undefined => undefined,
            adjustPositionOfPanelProduct: (): undefined => undefined,
        } as unknown as CabinetMesh['userData'];

        clearStoredPlaceholderColors();
        scene.add(group);
        applyPlaceholderVerticalPlacement(
            group,
            scene.children as CabinetMesh[]
        );
        group.userData.savedPosition = group.position.clone();
        return group;
    });
};

/**
 * Adds a placeholder (Fridge or Oven) from the Add popup, positioned next to the selected cabinet
 * on the chosen side, same way as loading a product.
 * @param {PlaceholderConfig} config - The placeholder configuration.
 * @param {THREE.Scene} scene - The Three.js scene to add the placeholder to.
 * @param {'left' | 'right'} positioningSide - The side to position the placeholder relative to the selected cabinet.
 * @param {CabinetMesh} selectedCabinetGroup - The selected cabinet to position next to.
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef - Reference to the list of cabinets.
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef - Reference to the list of placeholders.
 * @return {Promise<void>} A promise that resolves when the placeholder is added.
 */
export const addPlaceholderFromPopup = async (
    config: PlaceholderConfig,
    scene: THREE.Scene,
    positioningSide: 'left' | 'right',
    selectedCabinetGroup: CabinetMesh,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>
): Promise<void> => {
    setCabinetPositioningFlags(selectedCabinetGroup, positioningSide);

    const uniqueId = makePlaceholderCopyId(
        getNextPlaceholderId(config, placeholderListRef),
        0
    );
    const group = await loadPlaceholderGLB(
        {...config, id: uniqueId, initialX: 0, initialZ: 0},
        scene
    );
    applyConfigDimensions(group, config);
    stampPlaceholderProductIdentity(group, uniqueId, 1);

    const existingCabinets = cabinetGroupListRef.current;
    const {position, positionedNextToTarget} = getTargetPositionNextToCabinet(
        existingCabinets,
        group,
        group.position.clone()
    );

    if (positionedNextToTarget) {
        group.position.copy(position);
    }

    const existingItems = [
        ...cabinetGroupListRef.current,
        ...placeholderListRef.current,
    ];
    applyPlaceholderVerticalPlacement(group, existingItems);
    resolvePlaceholderSpawnOverlaps(group, existingItems);

    clearCabinetPositioningFlags(selectedCabinetGroup);
    commitPlaceholder(group, placeholderListRef, cabinetGroupListRef, {
        resetPointer: true,
    });
};

/**
 * Adds a placeholder (Fridge or Oven) using the same spawn placement as floor
 * cabinets: back wall, then to the right of existing items, wrapping to a new
 * row when the wall is full.
 * @param {PlaceholderConfig} config - The placeholder configuration
 * @param {THREE.Scene} scene - The Three.js scene to add the placeholder to
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef - Reference to the list of placeholders
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef - Reference to the list of cabinets
 * @param {AddPlaceholderOptions=} options Copy id / quantity / product-event sync
 * @return {Promise<CabinetMesh>} The added placeholder group
 */
export const addPlaceholderAtDefault = async (
    config: PlaceholderConfig,
    scene: THREE.Scene,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    options: AddPlaceholderOptions = {}
): Promise<CabinetMesh> => {
    const quantity = clampPlaceholderQuantity(options.quantity ?? 1);
    const uniqueId =
        options.id ??
        makePlaceholderCopyId(
            getNextPlaceholderId(config, placeholderListRef),
            0
        );
    const group = await loadPlaceholderGLB(
        {
            ...config,
            id: uniqueId,
            // Temporary origin; determinePlaceholderSpawnLocation repositions.
            initialX: 0,
            initialZ: 0,
        },
        scene
    );

    applyConfigDimensions(group, config);
    stampPlaceholderProductIdentity(group, uniqueId, quantity);

    const existingItems = [
        ...cabinetGroupListRef.current,
        ...placeholderListRef.current,
    ];
    determinePlaceholderSpawnLocation(group, existingItems);
    applyPlaceholderVerticalPlacement(group, existingItems);
    resolvePlaceholderSpawnOverlaps(group, existingItems);

    commitPlaceholder(group, placeholderListRef, cabinetGroupListRef, {
        sync: options.sync !== false,
        resetPointer: options.sync !== false,
    });
    return group;
};

/**
 * Add `quantity` visual copies that count as one placeholder product
 * (same as a cabinet with `cabinet_quantity` > 1).
 *
 * @param {PlaceholderConfig} config Catalog config
 * @param {THREE.Scene} scene Scene to add into
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @param {number} quantity How many copies to render
 * @return {Promise<CabinetMesh[]>} The copies, in index order
 */
export const addPlaceholderQuantityGroup = async (
    config: PlaceholderConfig,
    scene: THREE.Scene,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    quantity: number
): Promise<CabinetMesh[]> => {
    const count = clampPlaceholderQuantity(quantity);
    const groupId = getNextPlaceholderId(config, placeholderListRef);
    const copies: CabinetMesh[] = [];

    for (let index = 0; index < count; index++) {
        const group = await addPlaceholderAtDefault(
            config,
            scene,
            placeholderListRef,
            cabinetGroupListRef,
            {
                id: makePlaceholderCopyId(groupId, index),
                quantity: count,
                sync: index === count - 1,
            }
        );
        copies.push(group);
    }

    return copies;
};

/**
 * Grow or shrink a placeholder product's visual copies to match `quantity`,
 * the same way updating cabinet quantity adds/removes duplicate meshes.
 *
 * @param {CabinetMesh} source Any copy of the product
 * @param {number} quantity Target copy count (>= 1)
 * @param {THREE.Scene} scene Scene for new copies
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @return {Promise<CabinetMesh[]>} Members after the update
 */
export const applyPlaceholderGroupQuantity = async (
    source: CabinetMesh,
    quantity: number,
    scene: THREE.Scene,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>
): Promise<CabinetMesh[]> => {
    const count = clampPlaceholderQuantity(quantity);
    const groupId = getPlaceholderGroupId(source);
    let members = findPlaceholderGroupOf(placeholderListRef.current, source);

    if (count < members.length) {
        const removable = members
            .filter((item) => item.uuid !== source.uuid)
            .sort(
                (left, right) =>
                    getPlaceholderCopyIndex(right) -
                    getPlaceholderCopyIndex(left)
            );
        const extra = members.length - count;
        removable.slice(0, extra).forEach((item) => {
            removePlaceholderFromScene(
                item,
                placeholderListRef,
                cabinetGroupListRef,
                true
            );
        });
        members = findPlaceholderGroupOf(placeholderListRef.current, source);
    } else if (count > members.length) {
        const config = configFromPlaceholderMesh(source);
        if (!config) {
            setPlaceholderGroupQuantityValue(members, count);
            return members;
        }
        const startIndex = getMaxPlaceholderCopyIndex(members) + 1;
        const toAdd = count - members.length;
        for (let offset = 0; offset < toAdd; offset++) {
            await addPlaceholderAtDefault(
                config,
                scene,
                placeholderListRef,
                cabinetGroupListRef,
                {
                    id: makePlaceholderCopyId(groupId, startIndex + offset),
                    quantity: count,
                    sync: offset === toAdd - 1,
                }
            );
        }
        members = findPlaceholderGroupOf(placeholderListRef.current, source);
    }

    setPlaceholderGroupQuantityValue(members, count);
    members.forEach((item) => {
        autoSave(item, true);
    });
    return members;
};

/**
 * Duplicate a placeholder beside the source — same idea as floor cabinet
 * duplicate: keep Y/Z/rotation/direction, place AABB to the right of source.
 * @param {CabinetMesh} source Placeholder to copy
 * @param {PlaceholderConfig} config Catalog config for the clone
 * @param {THREE.Scene} scene Scene to add the clone into
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @return {Promise<CabinetMesh>} The duplicated placeholder group
 */
export const duplicatePlaceholderBesideSource = async (
    source: CabinetMesh,
    config: PlaceholderConfig,
    scene: THREE.Scene,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>
): Promise<CabinetMesh> => {
    const uniqueId = makePlaceholderCopyId(
        getNextPlaceholderId(config, placeholderListRef),
        0
    );
    const group = await loadPlaceholderGLB(
        {
            ...config,
            id: uniqueId,
            initialX: 0,
            initialZ: 0,
        },
        scene
    );

    applyPlaceholderDimensions(group, getPlaceholderDimensionsMm(source));
    stampPlaceholderProductIdentity(group, uniqueId, 1);

    // Match source pose first (same as floor cabinet duplicate), then slide
    // AABB so the clone sits immediately to the right on the same row.
    group.rotation.copy(source.rotation);
    group.userData.currentDirection =
        source.userData.currentDirection || 'front';
    group.position.copy(source.position);
    group.updateMatrixWorld(true);
    source.updateMatrixWorld(true);

    const sourceBox = computeWorldBoundsExcludingAnimatedDoorParts(source);
    let ownBox = computeWorldBoundsExcludingAnimatedDoorParts(group);

    // Align back edges on the same row
    group.position.z += sourceBox.min.z - ownBox.min.z;
    group.updateMatrixWorld(true);
    ownBox = computeWorldBoundsExcludingAnimatedDoorParts(group);

    // Place to the right of source (cabinet duplicate rule)
    group.position.x +=
        sourceBox.max.x + DUPLICATE_PLACEMENT_GAP - ownBox.min.x;
    group.updateMatrixWorld(true);

    // If past the right wall, fall back to default next-in-row spawn
    const dimensions = getRoomDimensionsComputed();
    const placementBounds = getDuplicateFloorPlacementBounds(Camera.scene);
    const pad = DUPLICATE_FLOOR_BOUNDS_PADDING;
    const roomMaxX = placementBounds
        ? placementBounds.max.x - pad
        : dimensions.roomWidth / 2 - DUPLICATE_FLOOR_WALL_MARGIN;
    const existingItems = [
        ...cabinetGroupListRef.current,
        ...placeholderListRef.current,
    ];
    const placedBox = computeWorldBoundsExcludingAnimatedDoorParts(group);
    if (placedBox.max.x > roomMaxX) {
        determinePlaceholderSpawnLocation(group, existingItems);
    }
    resolvePlaceholderSpawnOverlaps(group, existingItems);

    commitPlaceholder(group, placeholderListRef, cabinetGroupListRef, {
        resetPointer: true,
    });
    return group;
};

type RestorePlaceholderItem = {
    id: string;
    position: {x: number; y: number; z: number};
    rotation: {x: number; y: number; z: number};
    currentDirection?: string | null;
    actualDimensions?: {width: number; height: number; depth: number};
    quantity?: number;
    template?: string | null;
    name?: string;
    category?: string;
    style?: number;
    placeholderFaceId?: number | null;
    layoutPlaceholderId?: number;
};

const readEulerAxis = (
    value: Record<string, unknown> | {x?: number; y?: number; z?: number},
    axis: 'x' | 'y' | 'z'
): number => {
    const record = value as {x?: number; y?: number; z?: number};
    const underscored = value as Record<string, unknown>;
    let plain = 0;
    let fallback = 0;
    if (axis === 'x') {
        plain = Number(record.x);
        fallback = Number(underscored._x);
    } else if (axis === 'y') {
        plain = Number(record.y);
        fallback = Number(underscored._y);
    } else {
        plain = Number(record.z);
        fallback = Number(underscored._z);
    }
    if (!Number.isNaN(plain)) return plain;
    return Number.isNaN(fallback) ? 0 : fallback;
};

const mapApiPlaceholder = (
    item: LayoutRoomPlaceholder,
    facesById: Map<number, PlaceholderFace>
): RestorePlaceholderItem | null => {
    if (item.id == null || !item.position || !item.rotation) return null;

    // The layouts response carries the catalogue entry, which still resolves
    // once an admin retires it from the live catalogue.
    const face = item.placeholder ?? facesById.get(item.placeholder_id);

    // The catalogue owns the mesh and the details, but the size is whatever the
    // customer saved: a resize has to survive a reload. The face is only the
    // fallback, for placements stored before a size was kept.
    const width = item.width ?? face?.width;
    const height = item.height ?? face?.height;
    const depth = item.depth ?? face?.depth;

    const currentDirection = item.current_direction ?? null;

    return {
        id: placeholderSceneId(item),
        position: {
            x: readEulerAxis(item.position, 'x'),
            y: readEulerAxis(item.position, 'y'),
            z: readEulerAxis(item.position, 'z'),
        },
        rotation: {
            x: readEulerAxis(item.rotation, 'x'),
            y: readEulerAxis(item.rotation, 'y'),
            z: readEulerAxis(item.rotation, 'z'),
        },
        currentDirection,
        actualDimensions:
            width != null && height != null && depth != null
                ? normalizePlaceholderDimensionsMm({
                      width: Number(width),
                      height: Number(height),
                      depth: Number(depth),
                  })
                : undefined,
        template: face?.template,
        name: face?.name,
        category: face?.category,
        style: face?.style,
        placeholderFaceId: item.placeholder_id,
        layoutPlaceholderId: item.id,
    };
};

const resolveRestoreConfig = (
    item: RestorePlaceholderItem
): PlaceholderConfig | null => {
    if (item.template) {
        // Load at GLB scale; saved actualDimensions are applied after load.
        return {
            url: item.template,
            id: item.id,
            name: item.name,
            category: item.category,
            style: item.style,
            placeholderFaceId: item.placeholderFaceId ?? undefined,
        };
    }

    // Legacy API ids without a catalog template
    const baseId = String(item.id).startsWith('fridge')
        ? 'placeholder-fridge'
        : String(item.id).startsWith('oven')
          ? 'placeholder-oven'
          : null;
    if (!baseId) return null;

    const config = PLACEHOLDER_CONFIGS.find((c) => c.id === baseId);
    return config ? {...config, id: item.id} : null;
};

/**
 * Restore placeholders for a room from the layout API.
 * Any meshes already in the scene belong to the previous room and are removed
 * first, without deleting their saved layout records.
 * @param {THREE.Scene} scene Scene to restore into
 * @param {React.MutableRefObject<CabinetMesh[]>} placeholderListRef Placeholder list
 * @param {React.MutableRefObject<CabinetMesh[]>} cabinetGroupListRef Cabinet list
 * @param {LayoutRoomPlaceholder[]} apiPlaceholders Placeholders from this room. An empty list leaves the room empty.
 * @param {function(): boolean} [isCurrent] False once a newer room load has started
 * @return {Promise<void>}
 */
export const restorePlaceholdersForRoom = async (
    scene: THREE.Scene,
    placeholderListRef: React.MutableRefObject<CabinetMesh[]>,
    cabinetGroupListRef: React.MutableRefObject<CabinetMesh[]>,
    apiPlaceholders: LayoutRoomPlaceholder[] = [],
    isCurrent: () => boolean = () => true
): Promise<void> => {
    if (!isCurrent()) return;
    clearPlaceholdersFromScene(placeholderListRef, cabinetGroupListRef);

    let facesById = new Map<number, PlaceholderFace>();
    try {
        const faces = await store
            .dispatch(
                placeholderFaceApi.endpoints.getPlaceholderFaces.initiate()
            )
            .unwrap();
        facesById = new Map(faces.map((face) => [face.id, face]));
    } catch {
        // Restore can still use denormalized layout fields / legacy configs
    }

    const stored = apiPlaceholders
        .map((item) => mapApiPlaceholder(item, facesById))
        .filter((item): item is RestorePlaceholderItem => item != null);

    if (!isCurrent() || stored.length === 0) return;

    for (const item of stored) {
        if (!isCurrent()) return;
        const config = resolveRestoreConfig(item);
        if (!config) continue;

        try {
            const group = await loadPlaceholderGLB(
                {
                    ...config,
                    id: item.id,
                    initialX: item.position.x,
                    initialZ: item.position.z,
                },
                scene
            );
            if (!isCurrent()) {
                placeholderListRef.current.push(group);
                clearPlaceholdersFromScene(
                    placeholderListRef,
                    cabinetGroupListRef
                );
                return;
            }

            (group.userData as PlaceholderUserData).layoutPlaceholderId =
                item.layoutPlaceholderId;

            applyRestoredPlaceholderPose(group, {
                position: item.position,
                rotation: item.rotation,
                currentDirection: item.currentDirection,
                actualDimensions: item.actualDimensions,
                quantity: item.quantity,
            });
            placeholderListRef.current.push(group);
            store.dispatch(registerPlaceholderId(item.id));
        } catch (error) {
            // eslint-disable-next-line no-console -- restore failure should not block room load
            console.error('Error restoring placeholder:', item.id, error);
        }
    }

    syncPlaceholderQuantitiesFromGroups(placeholderListRef.current);
    syncPlaceholderProductEvents(cabinetGroupListRef);
};
